import { useCallback, useLayoutEffect, useRef } from 'react';
import { CONTENT_ID_SELECTOR } from '@common-fe/common-fe';

const STORAGE_PREFIX = 'scroll-restoration';
/** Throttle window for persisting the scroll offset while the user scrolls. */
const SAVE_THROTTLE_MS = 150;

interface Options {
  /**
   * Unique key identifying the page/context whose scroll offset should be
   * remembered. Use something stable and specific — e.g. the full route path
   * including any id (`/reports/delinquent-claims/123`). A different key means a
   * different slot, so opening a different details page starts at the top
   * (the previous page's offset is untouched and restored only when you return
   * to that exact key).
   */
  key: string;
  /**
   * Id of the scrollable container element. Defaults to the app content
   * wrapper (`content_wrapper`), which is what the app scrolls.
   */
  containerId?: string;
  /** When false, the hook stops saving/restoring (e.g. behind a feature flag). */
  enabled?: boolean;
  /**
   * When false, the offset is still saved but not restored on mount. Useful if
   * you only want to persist without auto-scrolling. Defaults to true.
   */
  restore?: boolean;
  /**
   * @deprecated No longer used. Restoration is driven off the scroll container
   * appearing/rebuilding in the DOM, not off a loading flag. Still accepted so
   * existing call sites keep compiling; passing it has no effect.
   */
  isLoading?: boolean;
}

const buildStorageKey = (key: string) => `${STORAGE_PREFIX}:${key}`;

const readOffset = (key: string): number | null => {
  try {
    const raw = window.sessionStorage.getItem(buildStorageKey(key));
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // sessionStorage can throw (private mode, quota, disabled). Fail silently.
    return null;
  }
};

const writeOffset = (key: string, offset: number) => {
  try {
    window.sessionStorage.setItem(buildStorageKey(key), String(offset));
  } catch {
    // Ignore storage failures — scroll memory is a nice-to-have, not critical.
  }
};

const clearOffset = (key: string) => {
  try {
    window.sessionStorage.removeItem(buildStorageKey(key));
  } catch {
    // Ignore storage failures.
  }
};

/**
 * Remove stored scroll offsets. Pass a `keyPrefix` to clear only the entries
 * whose key starts with it (e.g. `'/employee/'` to forget every employee page),
 * or omit it to clear all remembered scroll positions. Useful when returning to
 * a list page should reset the remembered scroll of its detail pages.
 */
export const clearScrollRestoration = (keyPrefix?: string) => {
  try {
    const fullPrefix = keyPrefix === undefined ? `${STORAGE_PREFIX}:` : buildStorageKey(keyPrefix);
    // Collect first, then remove — removing while iterating shifts indices.
    const toRemove: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const storageKey = window.sessionStorage.key(i);
      if (storageKey && storageKey.startsWith(fullPrefix)) {
        toRemove.push(storageKey);
      }
    }
    toRemove.forEach((storageKey) => window.sessionStorage.removeItem(storageKey));
  } catch {
    // Ignore storage failures.
  }
};

/**
 * Remembers the scroll offset of a container for a specific page and restores
 * it when the user returns to that same page. Backed by `sessionStorage`, so it
 * survives full reloads and browser back/forward within the tab's lifetime, and
 * clears automatically when the tab is closed.
 *
 * Because the saved offset is keyed by the caller-supplied `key`, remembering is
 * scoped per page: opening a different details page uses a different key and
 * therefore starts at the top, while the previous page's offset is preserved for
 * when you navigate back to it.
 *
 * Restoration is resilient to pages that render a preloader first and then grow:
 * it waits (via `MutationObserver`) for the container to appear, then re-applies
 * the saved offset (via `ResizeObserver`) as the content expands to its final
 * height, until the offset is reached or the user scrolls manually. This also
 * naturally wins over the global `ScrollToTop`, which resets the app content
 * wrapper to the top on every route change.
 *
 * @returns helpers to manually clear the stored offset for this key.
 */
/**
 * How long (ms) to keep trying to restore while the page grows. Pages that load
 * data render short first (a small spinner) and expand as content streams in;
 * the saved offset can only be applied once the container is tall enough. We
 * keep re-applying on size changes until this cap, then give up.
 */
const RESTORE_WINDOW_MS = 5000;
/** Tolerance (px) when deciding whether the saved offset was actually reached. */
const RESTORE_TOLERANCE = 2;

const useScrollRestoration = ({
  key,
  containerId = CONTENT_ID_SELECTOR,
  enabled = true,
  restore = true,
  isLoading,
}: Options) => {
  const throttleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestOffset = useRef<number>(0);

  const clear = useCallback(() => clearOffset(key), [key]);

  // `isLoading` is accepted for backwards compatibility but no longer drives the
  // restore: tying restoration to loading-flag timing proved unreliable on pages
  // whose loading state is composed of several out-of-order async sources and
  // whose scroll container is torn down and rebuilt (e.g. switching between
  // employees on the same route). Restoration is instead driven off the scroll
  // container appearing/rebuilding in the DOM (see the MutationObserver below).
  void isLoading;

  useLayoutEffect(() => {
    if (!enabled) return undefined;

    const saved = restore ? readOffset(key) : null;

    let container: HTMLElement | null = null;
    let handleScroll: (() => void) | null = null;
    let userGesture: (() => void) | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let rafId: number | null = null;

    // A genuine user gesture (wheel/touch/keydown) — NOT a programmatic or
    // layout-driven scroll — is what ends restoration and enables saving. This
    // is the crux of the fix: without it, the reset/scroll events that fire
    // while the page reloads (which report scrollTop 0) would both cancel
    // restore and overwrite the good saved value with 0.
    let interactionStarted = false;
    // Restoration is "settled" once we reached the target, the page can't scroll
    // further after the grow window, or the user interacted. Only then may we
    // start persisting scrollTop — this prevents clobbering the saved value with
    // the transient 0 the container reports during the async reload.
    let settled = saved === null;
    const restoreDeadline = Date.now() + RESTORE_WINDOW_MS;

    const stopRestoring = () => {
      settled = true;
      resizeObserver?.disconnect();
      resizeObserver = null;
      if (rafId !== null && typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // Apply the saved offset once and report whether restoration is now done.
    // "Done" means we reached the target, OR the page genuinely cannot scroll
    // any further and we are past the deadline (so a page that will never grow
    // tall enough doesn't keep trying forever).
    const applyRestore = (): boolean => {
      if (!container || settled || interactionStarted) return true;
      container.scrollTo({ top: saved as number, behavior: 'auto' });
      const reachedTarget = container.scrollTop >= (saved as number) - RESTORE_TOLERANCE;
      const cannotScrollFurther =
        container.scrollHeight - container.clientHeight <= container.scrollTop + RESTORE_TOLERANCE;
      return reachedTarget || (cannotScrollFurther && Date.now() >= restoreDeadline);
    };

    // Self-correcting restore loop. Instead of guessing WHEN the content has
    // reached its final height (fixed delays / a single ResizeObserver tick,
    // both of which raced the async content and failed intermittently), we
    // re-apply the saved offset every animation frame until it actually sticks:
    // the target is reached, the deadline passes, or the user takes over. This
    // is resilient to content that streams in over many frames and to the effect
    // re-running on loading transitions, because each run owns its own loop and
    // the cleanup cancels it.
    const runRestoreLoop = () => {
      if (!container || settled || interactionStarted || saved === null) return;

      if (typeof requestAnimationFrame === 'undefined') {
        // No rAF (SSR / jsdom without a shim): fall back to the ResizeObserver
        // safety net and a single immediate apply.
        if (applyRestore()) {
          stopRestoring();
          return;
        }
        if (typeof ResizeObserver !== 'undefined' && !resizeObserver) {
          resizeObserver = new ResizeObserver(() => {
            if (applyRestore()) stopRestoring();
          });
          resizeObserver.observe(container);
        }
        return;
      }

      const tick = () => {
        rafId = null;
        if (!container || settled || interactionStarted) return;
        const done = applyRestore();
        if (done || Date.now() >= restoreDeadline) {
          stopRestoring();
          return;
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    };

    const persist = (offset: number) => {
      latestOffset.current = offset;
      if (throttleTimer.current !== null) return;
      throttleTimer.current = setTimeout(() => {
        throttleTimer.current = null;
        writeOffset(key, latestOffset.current);
      }, SAVE_THROTTLE_MS);
    };

    const detach = () => {
      if (!container) return;
      if (handleScroll) container.removeEventListener('scroll', handleScroll);
      if (userGesture) {
        container.removeEventListener('wheel', userGesture);
        container.removeEventListener('touchmove', userGesture);
        container.removeEventListener('keydown', userGesture);
      }
      handleScroll = null;
      userGesture = null;
      container = null;
    };

    const attach = (el: HTMLElement) => {
      container = el;

      // A raw input gesture (wheel/touch/keydown) lets the user INTERRUPT an
      // in-progress restore so we don't yank them back to the saved offset. It
      // no longer gates saving — saving is driven purely by scroll events once
      // restoration has settled (see handleScroll). This is the fix for the
      // reported bug: scrolling via a sidebar click moves the container
      // programmatically (no wheel/touch/keydown), so gating saves on a raw
      // gesture meant those positions were never stored and nothing could be
      // restored.
      userGesture = () => {
        if (!interactionStarted) {
          interactionStarted = true;
          stopRestoring();
        }
        // Saving is handled by handleScroll (which also covers programmatic
        // scrolls). Here we only need to interrupt an in-progress restore; and
        // when restore is off we must not record anything.
        if (restore) persist(el.scrollTop);
      };
      el.addEventListener('wheel', userGesture, { passive: true });
      el.addEventListener('touchmove', userGesture, { passive: true });
      el.addEventListener('keydown', userGesture);

      handleScroll = () => {
        // Persist on ANY scroll once restoration has settled — including
        // programmatic scrolls from clicking the sidebar, which don't emit a raw
        // input gesture. We still ignore scrolls while restoration is in
        // progress (`!settled`), which is what stops the reload's transient
        // scrollTop=0 from overwriting the saved offset. When `restore` is false
        // the page is running its own autoscroll (e.g. deep-link to a module);
        // we must not record that scroll as the remembered position.
        if (!settled || !restore) return;
        persist(el.scrollTop);
      };
      el.addEventListener('scroll', handleScroll, { passive: true });

      // Kick off the self-correcting restore loop against this (possibly freshly
      // rebuilt) container. The loop no longer depends on a loading signal: it
      // runs whenever a real container is present and keeps re-applying until it
      // sticks, the deadline passes, or the user takes over.
      if (saved !== null) {
        runRestoreLoop();
      }
    };

    // Watch the DOM for the container appearing — and, crucially, for it being
    // torn down and REBUILT. On this app's Employee page the component is reused
    // across employee switches (same route, different id): the scroll container
    // is unmounted during the preloader and a brand-new element is created when
    // the next employee's content renders. A long-lived observer re-attaches and
    // re-runs restoration against each new element, instead of relying on a
    // loading-flag edge that the noisy, out-of-order loading sources don't
    // deliver cleanly. We only need to act while there's still something to
    // restore and the user hasn't taken over.
    const syncContainer = () => {
      // Once the user has taken over we never touch the scroll again — but a
      // mere "reached target" (`settled`) must NOT permanently stop us, because
      // the container can be rebuilt (preloader swap / refetch) and the new
      // element starts at the top and needs restoring again.
      if (interactionStarted) return;
      const found = document.getElementById(containerId);
      if (found === container) return;
      // Container identity changed (appeared, or rebuilt). Re-wire to the new one
      // and give it a fresh restore attempt.
      detach();
      if (rafId !== null && typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      resizeObserver?.disconnect();
      resizeObserver = null;
      settled = saved === null;
      if (found) attach(found);
    };

    const existing = document.getElementById(containerId);
    if (existing) {
      attach(existing);
    }
    if (typeof MutationObserver !== 'undefined') {
      // Keep observing for the whole effect lifetime so container rebuilds (not
      // just the first appearance) re-trigger restoration.
      mutationObserver = new MutationObserver(syncContainer);
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      if (rafId !== null && typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(rafId);
      }
      if (container) {
        if (handleScroll) container.removeEventListener('scroll', handleScroll);
        if (userGesture) {
          container.removeEventListener('wheel', userGesture);
          container.removeEventListener('touchmove', userGesture);
          container.removeEventListener('keydown', userGesture);
        }
      }
      if (throttleTimer.current !== null) {
        clearTimeout(throttleTimer.current);
        throttleTimer.current = null;
      }
      // Persist the final position on teardown — but only once restoration has
      // settled, so we never overwrite a good saved offset with the transient 0
      // a container reports before/while restore runs. Gating on `settled`
      // (rather than a raw user gesture) means a position reached by clicking the
      // sidebar is saved too, which is what was previously lost.
      if (settled && restore && container) {
        writeOffset(key, container.scrollTop);
      }
    };
  }, [enabled, restore, key, containerId]);

  return { clear };
};

export default useScrollRestoration;
